import { supabase } from "@/api/supabaseClient";
import {
  CURRENT_PRIVACY_NOTICE_VERSION,
  type FinderContactBaseline,
  type PrivacyProvisionKind,
} from "@/ui/finder/finderMode";

export { CURRENT_PRIVACY_NOTICE_VERSION };
export type { PrivacyProvisionKind };

export type ContactRole =
  | "finder"
  | "courier"
  | "release_contact"
  | "external_hibernate"
  | "other";

export type ContactRow = {
  id: string;
  organization_id: string;
  display_name: string;
  notes: string | null;
  privacy_notice_version: string | null;
  privacy_documented_at: string | null;
  privacy_documented_by: string | null;
  privacy_provision_kind: PrivacyProvisionKind | null;
  is_active: boolean;
  client_contact_id: string | null;
};

export type CaseFinderView = {
  has_finder: boolean;
  can_read_pii: boolean;
  contact_id?: string;
  display_name?: string;
  notes?: string | null;
  is_active?: boolean;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  preferred_channel?: string | null;
  privacy_notice_version?: string | null;
  privacy_documented_at?: string | null;
  privacy_documented_by?: string | null;
  privacy_provision_kind?: PrivacyProvisionKind | null;
};

export type ContactSearchHit = {
  id: string;
  display_name: string;
  is_active: boolean;
  phone: string | null;
  email: string | null;
};

export type VisibleCaseSearchHit = {
  case_id: string;
  case_number: string | null;
  name: string | null;
  status: string;
};

export type UpsertContactInput = {
  organizationId: string;
  id?: string | null;
  displayName?: string | null;
  notes?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  preferredChannel?: string | null;
  privacyNoticeVersion?: string | null;
  privacyProvisionKind?: PrivacyProvisionKind | null;
  privacyDocumented?: boolean | null;
  isActive?: boolean;
  clientContactId?: string | null;
  matchedContactId?: string | null;
  /** Bekannte Felder bei Wiederverwendung. Steuert, ob upsert nötig ist. */
  reuseBaseline?: FinderContactBaseline | null;
};

export class ContactsApi {
  static async upsertContact(input: UpsertContactInput): Promise<ContactRow> {
    const { data, error } = await supabase.rpc("upsert_contact", {
      p_organization_id: input.organizationId,
      p_id: input.id ?? null,
      p_display_name: input.displayName ?? null,
      p_notes: input.notes ?? null,
      p_phone: input.phone ?? null,
      p_email: input.email ?? null,
      p_address: input.address ?? null,
      p_preferred_channel: input.preferredChannel ?? null,
      p_privacy_notice_version: input.privacyNoticeVersion ?? null,
      p_privacy_provision_kind: input.privacyProvisionKind ?? null,
      p_privacy_documented: input.privacyDocumented ?? null,
      p_is_active: input.isActive ?? true,
      p_client_contact_id: input.clientContactId ?? null,
    });
    if (error) throw error;
    return data as ContactRow;
  }

  static async linkCaseContact(input: {
    caseId: string;
    contactId: string;
    roles?: ContactRole[];
    isPrimaryFinder?: boolean;
  }) {
    const { data, error } = await supabase.rpc("link_case_contact", {
      p_case_id: input.caseId,
      p_contact_id: input.contactId,
      p_roles: input.roles ?? ["finder"],
      p_is_primary_finder: input.isPrimaryFinder ?? true,
    });
    if (error) throw error;
    return data;
  }

  static async getCaseFinderView(caseId: string): Promise<CaseFinderView> {
    const { data, error } = await supabase.rpc("get_case_finder_view", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? { has_finder: false, can_read_pii: false }) as CaseFinderView;
  }

  static async searchContacts(
    organizationId: string,
    query: string
  ): Promise<ContactSearchHit[]> {
    const { data, error } = await supabase.rpc("search_contacts", {
      p_organization_id: organizationId,
      p_query: query,
    });
    if (error) throw error;
    return (data ?? []) as ContactSearchHit[];
  }

  static async matchExistingContacts(input: {
    organizationId: string;
    phone?: string | null;
    email?: string | null;
  }): Promise<ContactSearchHit[]> {
    const { data, error } = await supabase.rpc("match_existing_contacts", {
      p_organization_id: input.organizationId,
      p_phone: input.phone ?? null,
      p_email: input.email ?? null,
    });
    if (error) throw error;
    return (data ?? []) as ContactSearchHit[];
  }

  static async searchVisibleCases(query: string): Promise<VisibleCaseSearchHit[]> {
    const { data, error } = await supabase.rpc("search_visible_cases", {
      p_query: query,
    });
    if (error) throw error;
    return (data ?? []) as VisibleCaseSearchHit[];
  }
}
